import { z } from "zod";

/**
 * Primitive building blocks for the Website Definition.
 *
 * Rules:
 * - No transforms, no defaults: the schema must export losslessly to JSON Schema
 *   (the Go API validates with the exported file).
 * - Every string is length-capped. Every object is strict (additionalProperties: false).
 * - URLs are allow-listed by pattern. The renderer re-checks them at render time.
 */

export const ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;
export const id = () => z.string().regex(ID_PATTERN);

export const text = (max: number) => z.string().max(max);
export const requiredText = (max: number) => z.string().min(1).max(max);

/**
 * Links a visitor can follow. No javascript:, data:, vbscript:, protocol-relative, or whitespace.
 * Patterns must stay RE2-compatible (no lookarounds/backrefs): Go validates with the exported JSON Schema.
 */
export const SAFE_HREF_PATTERN =
  /^(?:https?:\/\/[^\s<>"'`\\]+|mailto:[^\s<>"'`\\]+|tel:\+?[0-9() -]{3,20}|#[a-z0-9][a-z0-9-]{0,63}|\/(?:[A-Za-z0-9\-._~%?=&#][A-Za-z0-9\-/._~%?=&#]*)?)$/;
export const href = () => z.string().max(2048).regex(SAFE_HREF_PATTERN);

/** Image sources: https URLs or our own /media paths. Never data:, never http. */
export const IMAGE_SRC_PATTERN = /^(?:https:\/\/[^\s<>"'`\\()]+|\/media\/[A-Za-z0-9\-/._]+)$/;

export const image = () =>
  z.strictObject({
    src: z.string().max(2048).regex(IMAGE_SRC_PATTERN),
    alt: text(200),
    assetId: z.string().uuid().optional(),
    width: z.number().int().min(1).max(12000).optional(),
    height: z.number().int().min(1).max(12000).optional(),
    /** Focal point in percent, used for object-position when cropping. */
    focal: z
      .strictObject({ x: z.number().min(0).max(100), y: z.number().min(0).max(100) })
      .optional(),
  });

/** WhatsApp number normalized to international format without "+": 62812xxxxxxx. */
export const WA_PATTERN = /^62[1-9][0-9]{7,12}$/;
export const waNumber = () => z.string().regex(WA_PATTERN);

export const PHONE_PATTERN = /^\+?[0-9() -]{6,20}$/;
export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
export const TIME_PATTERN = /^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/;
/** Calendar date, YYYY-MM-DD (events). The renderer re-checks that the date actually exists. */
export const DATE_PATTERN = /^20[0-9]{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12][0-9]|3[01])$/;
/** Social handles, not URLs; the engine builds the URL. */
export const HANDLE_PATTERN = /^[A-Za-z0-9._-]{1,60}$/;
export const PAGE_SLUG_PATTERN = /^\/(?:[a-z0-9][a-z0-9-]{0,47})?$/;

/** Price in whole rupiah. */
export const price = () => z.number().int().min(0).max(10_000_000_000);

export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

export const hoursEntry = () =>
  z.strictObject({
    day: z.enum(WEEKDAYS),
    closed: z.boolean(),
    open: z.string().regex(TIME_PATTERN).optional(),
    close: z.string().regex(TIME_PATTERN).optional(),
  });

/**
 * Call to action. "whatsapp" opens wa.me with the site's number and the given message;
 * "link" follows href (safe-listed). Placeholders {{nama_usaha}} and {{produk}} are allowed in message.
 */
export const cta = () =>
  z.strictObject({
    label: requiredText(40),
    kind: z.enum(["whatsapp", "link"]),
    href: href().optional(),
    message: text(300).optional(),
  });
